import React from "react";
import { Switch, Route, Link, useParams, useRouteMatch } from "react-router-dom";
import { ChannelLogsPage } from "../pages/TwilioChannelLogsPage";
import { WorkerLogsPage } from "../pages/TwilioWorkerLogsPage";
import { hasPowFeature, POW_FEATURES } from "../services/powFeatures.service";
import { SubscriptionLicensesPage } from "../pages/SubscriptionLicensesPage";
import { SubscriptionDetailsPage } from "../pages/SubscriptionDetailsPage";
import { SubscriptionNamedResourcesPage } from "../pages/SubscriptionNamedResourcesPage";
import { PlatformEventsPage } from "../pages/PlatformEventsPage";
import { FeatureTogglesListPage } from "../pages/FeatureToggles/FeatureTogglesListPage";
import { FeatureToggleDetailsPage } from "../pages/FeatureToggles/FeatureToggleDetailsPage";
import { FeatureToggleAddPage } from "../pages/FeatureToggles/FeatureToggleAddPage";
import { OperationalConfigurationsListPage } from "../pages/OperationalConfigurations/OperationalConfigurationsListPage";
import { OperationalConfigurationDetailsPage } from "../pages/OperationalConfigurations/OperationalConfigurationDetailsPage";
import { OperationalConfigurationAddPage } from "../pages/OperationalConfigurations/OperationalConfigurationAddPage";
import { CurrentUserPage } from "../pages/CurrentUserPage";
import { SubscriptionListPage } from "../pages/SubscriptionListPage";
import { AllTwilioLogsPage } from "../pages/AllTwilioLogsPage";
import { CallLogsPage } from "../pages/TwilioCallLogsPage";
import { SubscriptionNavigationBar } from "../components/SubscriptionNavigationBar";
import { SubscriptionBreadCrumbComponent } from "../components/SubscriptionBreadCrumbComponent";
import { BasePageWrapper } from "../pages/BasePageWrapper";
import { ReportsListPage } from "../pages/Reports/ReportsListPage";
import { ReportAddPage } from "../pages/Reports/ReportsAddPage";
import { ReportDetailsPage } from "../pages/Reports/ReportsDetailsPage";
import { toReportList, toUserListPage, toTwilioInvoiceListPage } from "./navigation";
import { AdministrationNavigationBar } from "../components/Administration/AdministrationNavigationBar";
import { UserListPage } from "../pages/Administration/UserListPage";
import { UserEditPage } from "../pages/Administration/UserEditPage";
import { ServiceMessagingPage } from "../pages/Administration/ServiceMessagingPage";
import { TestReportsPage } from "../pages/Administration/TestReportsPage";
import { TwilioInvoiceDetailsPage } from "../pages/TwilioInvoice/TwilioInvoiceDetailsPage";
import { TwilioInvoicesPage } from "../pages/TwilioInvoice/TwilioInvoicesPage";
import { CLELoggerTestPage } from "../pages/Administration/CLELoggerTestPage";
import { ServiceLogLevelPage } from "../pages/Administration/ServiceLogLevelsPage";
const RenderPage = (pageElement: JSX.Element) => {
	return <BasePageWrapper>{pageElement}</BasePageWrapper>;
};

/**
 * The component is used to route to the various react pages in the package.
 * This component extracts the productId and SubscriptionId from the url and passes those fields into the page that corresponds to the URL.
 * The router also allows for an extra layer of security regarding pow features and prevents people from directly accessing pages they are not allowed to view via URL.
 */

export const ReactRouter = () => {
	return (
		<Switch>
			<Route path="/CurrentUser">{RenderPage(<CurrentUserPage />)}</Route>
			{hasPowFeature(POW_FEATURES.pdpAdminMenu) && (
				<Route path="/Administration">
					<AdministrationRoute />
				</Route>
			)}
			{hasPowFeature(POW_FEATURES.paPlatformEvents) && (
				<Route path="/Products/:productId/TwilioPlatformEvents">
					<PlatformEventsRoute />
				</Route>
			)}
			{hasPowFeature(POW_FEATURES.paFeatures) && (
				<Route path="/Products/:productId/FeatureToggles">
					<FeatureTogglesRoute />
				</Route>
			)}
			{hasPowFeature(POW_FEATURES.paConfigurations) && (
				<Route path="/Products/:productId/OperationalConfigurations">
					<OperationalConfigurationsRoute />
				</Route>
			)}
			{hasPowFeature(POW_FEATURES.paSubscriptions) && (
				<Route path="/Products/:productId/Subscriptions">
					<SubscriptionRoute />
				</Route>
			)}
			{hasPowFeature(POW_FEATURES.paReports) && (
				<Route path="/Products/:productId/Reports">
					<ReportsRoute />
				</Route>
			)}
			{hasPowFeature(POW_FEATURES.paInvoices) && (
				<Route path="/Products/:productId/Invoices">
					<TwilioInvoicesRoute />
				</Route>
			)}
		</Switch>
	);
};
function TwilioInvoicesRoute() {
	const { path } = useRouteMatch();
	const { productId } = useParams();
	return (
		<div>
			<Switch>
				<Route path={`${path}/:invoiceDate`}>
					<TwilioInvoiceDetailsRoute />
				</Route>
				<Route path={`${path}`}>{RenderPage(<TwilioInvoicesPage productId={productId} />)}</Route>
			</Switch>
		</div>
	);
}

function TwilioInvoiceDetailsRoute() {
	const { productId, invoiceDate } = useParams();
	return (
		<div>
			{RenderPage(<TwilioInvoiceDetailsPage productId={productId} invoiceDate={invoiceDate} />)}
			<div style={{ marginTop: 25, marginBottom: 25 }}>
				<Link to={toTwilioInvoiceListPage(productId)}>Back to Invoice List</Link>
			</div>
		</div>
	);
}

function AdministrationRoute() {
	const { path } = useRouteMatch();
	return (
		<div>
			<AdministrationNavigationBar />
			<div style={{ marginLeft: 75 }}>
				<Switch>
					{hasPowFeature(POW_FEATURES.pdpAdminUsers) && (
						<Route path={`${path}/users/:userPrincipalName`}>
							<AdministrationUsersRoute />
						</Route>
					)}
					{hasPowFeature(POW_FEATURES.pdpAdminUsers) && (
						<Route path={`${path}/users`}> {RenderPage(<UserListPage />)}</Route>
					)}
					<Route path={`${path}/servicemessaging`}>{RenderPage(<ServiceMessagingPage />)}</Route>
					{hasPowFeature(POW_FEATURES.pdpAdminCLELogger) && (
						<Route path={`${path}/CLELogger`}>{RenderPage(<CLELoggerTestPage />)}</Route>
					)}
					{hasPowFeature(POW_FEATURES.pdpAdminTestReports) && (
						<Route path={`${path}/testreports`}>{RenderPage(<TestReportsPage />)}</Route>
					)}
					{hasPowFeature(POW_FEATURES.pdpAdminServiceLogLevels_View) && (
						<Route path={`${path}/serviceloglevel`}>{RenderPage(<ServiceLogLevelPage />)}</Route>
					)}
				</Switch>
			</div>
		</div>
	);
}

function AdministrationUsersRoute() {
	const { userPrincipalName } = useParams();

	return (
		<div>
			{RenderPage(<UserEditPage userPrincipalName={userPrincipalName} />)}
			<div style={{ marginTop: 25, marginBottom: 25 }}>
				<Link to={toUserListPage()}>Back to User List</Link>
			</div>
		</div>
	);
}

function ReportsRoute() {
	const { path } = useRouteMatch();
	const { productId } = useParams();
	return (
		<Switch>
			<Route path={`${path}/new`}>
				<div>
					{RenderPage(<ReportAddPage productId={productId} />)}
					<div style={{ marginTop: 25, marginBottom: 25 }}>
						<Link to={toReportList(productId)}>Back to Reports</Link>
					</div>
				</div>
			</Route>
			<Route path={`${path}/:reportId`}>
				<ReportDetailsRoute />
			</Route>
			<Route path={`${path}`}>{RenderPage(<ReportsListPage productId={productId}></ReportsListPage>)}</Route>
		</Switch>
	);
}

function ReportDetailsRoute() {
	const { productId, reportId } = useParams();
	return (
		<div>
			{RenderPage(<ReportDetailsPage productId={productId} reportId={reportId} />)}
			<div style={{ marginTop: 25, marginBottom: 25 }}>
				<Link to={toReportList(productId)}>Back to Reports</Link>
			</div>
		</div>
	);
}

/**
 * This function handles the feature toggles page routing.
 */
function FeatureTogglesRoute() {
	const { path } = useRouteMatch();
	const { productId } = useParams();
	return (
		<Switch>
			<Route exact path={path}>
				{RenderPage(<FeatureTogglesListPage productId={productId} />)}
			</Route>
			{hasPowFeature(POW_FEATURES.paFeatures_Admin) && (
				<Route exact path={`${path}/new`}>
					<div>
						{RenderPage(<FeatureToggleAddPage productId={productId} />)}
						<div style={{ marginTop: 25, marginBottom: 25 }}>
							<Link to={`/Products/${productId}/FeatureToggles`}>Back to Feature Toggles</Link>
						</div>
					</div>
				</Route>
			)}
			<Route path={`${path}/:featureToggleId`}>
				<FeatureToggleDetailsRoute />
			</Route>
		</Switch>
	);
}

/**
 * This function handles the feature toggle details page routing.
 */
function FeatureToggleDetailsRoute() {
	const { productId, featureToggleId } = useParams();

	return (
		<div>
			{RenderPage(<FeatureToggleDetailsPage productId={productId} featureToggleId={featureToggleId} />)}
			<div style={{ marginTop: 25, marginBottom: 25 }}>
				<Link to={`/Products/${productId}/FeatureToggles`}>Back to Feature Toggles</Link>
			</div>
		</div>
	);
}

/**
 * This function handles the operational configuration page routing.
 */
function OperationalConfigurationsRoute() {
	const { path } = useRouteMatch();
	const { productId } = useParams();
	return (
		<Switch>
			<Route exact path={path}>
				{RenderPage(<OperationalConfigurationsListPage productId={productId} />)}
			</Route>

			{hasPowFeature(POW_FEATURES.paConfigurations_Admin) && (
				<Route exact path={`${path}/new`}>
					<div>
						{RenderPage(<OperationalConfigurationAddPage productId={productId} />)}
						<div style={{ marginTop: 25, marginBottom: 25 }}>
							<Link to={`/Products/${productId}/OperationalConfigurations`}>Back to Operational Configurations</Link>
						</div>
					</div>
				</Route>
			)}
			<Route path={`${path}/:operationalConfigurationId`}>
				<OperationalConfigurationDetailsRoute />
			</Route>
		</Switch>
	);
}

/**
 * This function handles the operational configuration details page routing.
 */
function OperationalConfigurationDetailsRoute() {
	const { productId, operationalConfigurationId } = useParams();

	return (
		<div>
			{RenderPage(
				<OperationalConfigurationDetailsPage
					productId={productId}
					operationalConfigurationId={operationalConfigurationId}
				/>,
			)}
			<div style={{ marginTop: 25, marginBottom: 25 }}>
				<Link to={`/Products/${productId}/OperationalConfigurations`}>Back to Operational Configurations</Link>
			</div>
		</div>
	);
}

/**
 * This function handles the platform event page routing.
 */
function PlatformEventsRoute() {
	const { productId } = useParams();
	return RenderPage(<PlatformEventsPage productId={productId}></PlatformEventsPage>);
}

/**
 * This function controls therouting for the subscription portion of POW.
 * If a user is not allowed to access a page certain page for a subscription,
 * the router will default the user to the subscription details page for said subscription.
 */
function SubscriptionRoute() {
	const { path } = useRouteMatch();
	const { productId } = useParams();
	return (
		<Switch>
			<Route exact path={path}>
				{RenderPage(<SubscriptionListPage productId={productId}></SubscriptionListPage>)}
			</Route>
			<Route path={`${path}/:subscriptionId`}>
				<SubscriptionNavigationRoute />
			</Route>
		</Switch>
	);
}

/**
 * This function takes care of routing to the various detail pages pertaining to a subscription.
 * It also sets up the navigation bar as well as the breadcrumbs and the "Back to Subscriptions" link at the bottom of the pages.
 */
function SubscriptionNavigationRoute() {
	const { path, url } = useRouteMatch();
	const { productId, subscriptionId } = useParams();
	return (
		<div>
			<SubscriptionNavigationBar url={url} path={path} />
			<div style={{ marginLeft: 75 }}>
				<SubscriptionBreadCrumbComponent
					productId={productId}
					subscriptionId={subscriptionId}
				></SubscriptionBreadCrumbComponent>
				<Switch>
					{hasPowFeature(POW_FEATURES.paSubscriptions_CallLogs) && (
						<Route exact path={`${path}/SubscriptionCallList`}>
							<span> {" > Call Logs"}</span>
							<div style={{ marginTop: 75 }}>
								{RenderPage(<CallLogsPage productId={productId} subscriptionId={subscriptionId}></CallLogsPage>)}
							</div>
							<div style={{ marginTop: 25, marginBottom: 25 }}>
								<Link to={`/Products/${productId}/Subscriptions`}>Back to Subscriptions </Link>
							</div>
						</Route>
					)}
					{hasPowFeature(POW_FEATURES.paSubscriptions_AllLogs) && (
						<Route exact path={`${path}/AllTwilioLogs`}>
							<span> {" > All Logs"}</span>
							<div style={{ marginTop: 75 }}>
								{RenderPage(
									<AllTwilioLogsPage productId={productId} subscriptionId={subscriptionId}></AllTwilioLogsPage>,
								)}
							</div>
							<div style={{ marginTop: 25, marginBottom: 25 }}>
								<Link to={`/Products/${productId}/Subscriptions`}>Back to Subscriptions </Link>
							</div>
						</Route>
					)}
					{hasPowFeature(POW_FEATURES.paSubscriptions_ChannelLogs) && (
						<Route exact path={`${path}/TwilioChannelList`}>
							<span> {" > Channel Logs"}</span>
							<div style={{ marginTop: 75 }}>
								{RenderPage(<ChannelLogsPage productId={productId} subscriptionId={subscriptionId}></ChannelLogsPage>)}
							</div>
							<div style={{ marginTop: 25, marginBottom: 25 }}>
								<Link to={`/Products/${productId}/Subscriptions`}>Back to Subscriptions </Link>
							</div>
						</Route>
					)}
					{hasPowFeature(POW_FEATURES.paSubscriptions_WorkerLogs) && (
						<Route exact path={`${path}/TwilioWorkerLogs`}>
							<span> {" > Worker Logs"}</span>
							<div style={{ marginTop: 75 }}>
								{RenderPage(<WorkerLogsPage productId={productId} subscriptionId={subscriptionId}></WorkerLogsPage>)}
							</div>
							<div style={{ marginTop: 25, marginBottom: 25 }}>
								<Link to={`/Products/${productId}/Subscriptions`}>Back to Subscriptions </Link>
							</div>
						</Route>
					)}
					{hasPowFeature(POW_FEATURES.paSubscriptions_Licenses) && (
						<Route exact path={`${path}/Licenses`}>
							<span> {" > Subscription Licenses"}</span>
							<div style={{ marginTop: 75 }}>
								{RenderPage(
									<SubscriptionLicensesPage
										productId={productId}
										subscriptionId={subscriptionId}
									></SubscriptionLicensesPage>,
								)}
							</div>
							<div style={{ marginTop: 25, marginBottom: 25 }}>
								<Link to={`/Products/${productId}/Subscriptions`}>Back to Subscriptions </Link>
							</div>
						</Route>
					)}
					{hasPowFeature(POW_FEATURES.paSubscriptions_NamedResources) && (
						<Route exact path={`${path}/NamedResources`}>
							<span> {" > Subscription Named Resources"}</span>
							<div style={{ marginTop: 75 }}>
								{RenderPage(
									<SubscriptionNamedResourcesPage
										productId={productId}
										subscriptionId={subscriptionId}
									></SubscriptionNamedResourcesPage>,
								)}
							</div>
							<div style={{ marginTop: 25, marginBottom: 25 }}>
								<Link to={`/Products/${productId}/Subscriptions`}>Back to Subscriptions </Link>
							</div>
						</Route>
					)}
					<Route path={path}>
						<span> {" > Subscription Details"}</span>
						<div style={{ marginTop: 75 }}>
							{RenderPage(
								<SubscriptionDetailsPage
									productId={productId}
									subscriptionId={subscriptionId}
								></SubscriptionDetailsPage>,
							)}
						</div>
						<div style={{ marginTop: 25, marginBottom: 25 }}>
							<Link to={`/Products/${productId}/Subscriptions`}>Back to Subscriptions </Link>
						</div>
					</Route>
				</Switch>
			</div>
		</div>
	);
}
